import React, { useState, useCallback, useMemo, useEffect } from 'react'

import { MemoizedEvent } from '..'
import { useAppDispatch, useAppSelector } from '../../../App/Hooks/reduxHooks'
import { useWindowDimensions } from '../../../Common/Hooks/useWindowDimensions'
import { DATE_STORE_KEY_FORMAT } from '../../../Common/settings/dateFormats'
import {
  calendarSettingsSelector,
  calendarStatusSelector,
  eventPositionChange,
  setOpenEventDetails,
} from '../../store/calendarSlice'
import { fetchBlocks } from '../../store/thunks/fetchBlocks'
import { fetchCalendar } from '../../store/thunks/fetchCalendar'
import {
  IAppointment,
  ICalendarDay,
  IEvent,
  IUpdateAppointmentDetailsForm,
  IUpdateBlockForm,
} from '../../types'
import { getDaysBetweenDates, getStartAndEndDate } from '../../utils'
import { calendarToEventsTransformer } from '../transformer/calendarToEventsTransformer'
import { calcColumnWidth } from '../utils/calcColumnWidth'
import { currentColumnCalculation } from '../utils/currentColumnCalculation'
import { filterEventsBySelectedDateRange } from '../utils/filterEventsBySelectedDateRange'

import { IProps } from './IProps'
import { DragAndDropArea } from './style'

const eventAreaCalc = {
  sideNavWidth: 78,
  hourColumnWidth: 40,
}

export const EventArea = ({
  filteredPersons,
  inActivePersonIds,
  amountOfColumns,
  timeDivision,
  selectedStartDate,
  amountOfDays,
  setCalculatedPixelsPerHour,
  activityMetaData,
}: IProps) => {
  const { windowWidth } = useWindowDimensions()

  const [activeEventGroupId, setActiveEventGroupId] = useState<
    string | undefined
  >()

  const dispatch = useAppDispatch()
  const events = useAppSelector(state =>
    filterEventsBySelectedDateRange(state, selectedStartDate, amountOfDays),
  )
  const calendarStatus = useAppSelector(calendarStatusSelector)
  const { zoomLevelValue } = useAppSelector(calendarSettingsSelector)
  const activeEventState = useAppSelector(eventPositionChange).state

  const [columnWidth, setColumnWidth] = useState<number>(
    calcColumnWidth(windowWidth, eventAreaCalc, amountOfColumns),
  )
  const [slotsPerHour] = useState<number>(60 / timeDivision)
  const [pixelsPerHour, setPixelsPerHour] = useState<number>(
    zoomLevelValue
      ? parseInt(zoomLevelValue, 10) * slotsPerHour
      : 30 * slotsPerHour,
  )

  const dateKeyArray = useMemo(() => {
    if (calendarStatus === 'loading') {
      return null
    }
    const dateRange = getStartAndEndDate(selectedStartDate, amountOfDays)
    const newDateKeyArray = getDaysBetweenDates(dateRange.start, dateRange.end)

    const currentDate = dateRange.start.clone()
    do {
      const currentDateKey = currentDate.format(DATE_STORE_KEY_FORMAT)
      if (!(currentDateKey in events)) {
        dispatch(fetchCalendar({ dateRange }))
        dispatch(fetchBlocks({ dateRange }))
        break
      }
      currentDate.add(1, 'day')
    } while (currentDate.isBefore(dateRange.end))

    setCalculatedPixelsPerHour(pixelsPerHour)
    return newDateKeyArray
  }, [
    calendarStatus,
    selectedStartDate,
    amountOfDays,
    setCalculatedPixelsPerHour,
    pixelsPerHour,
    events,
  ])

  const filteredPlannedEvents = useMemo<IEvent[]>(() => {
    console.log('TRIGGGER')
    if (!dateKeyArray) {
      return []
    }
    return calendarToEventsTransformer(
      events,
      activityMetaData,
      dateKeyArray,
      inActivePersonIds,
    )
  }, [activityMetaData, dateKeyArray, events, inActivePersonIds])

  useMemo(() => {
    const newColumnWidth: number = calcColumnWidth(
      windowWidth,
      eventAreaCalc,
      amountOfColumns,
    )
    setColumnWidth(newColumnWidth)
  }, [amountOfColumns, windowWidth])
  useMemo(() => {
    setPixelsPerHour(parseInt(zoomLevelValue, 10) * slotsPerHour)
  }, [slotsPerHour, zoomLevelValue])

  useEffect(() => {
    if (activeEventState !== 'idle') {
      setActiveEventGroupId(undefined)
    }
  }, [activeEventState])

  const onEventClicked = useCallback(
    (event: IEvent) => {
      setActiveEventGroupId(event.groupId)
      if (event.groupId === activeEventGroupId) {
        let eventDetails:
          | IUpdateAppointmentDetailsForm
          | IUpdateBlockForm
          | undefined
        const eventDate: string = event.startsAt.format(DATE_STORE_KEY_FORMAT)
        // TODO events should be stored difrently in store
        const selectedDateEvents: ICalendarDay = events[eventDate]
        const typeId: number = parseInt(event.groupId.split('_')[0], 10)

        switch (event.type) {
          case 'appointment':
            {
              const foundAppointment = selectedDateEvents.appointments.find(
                (appointment: IAppointment) =>
                  appointment.appointmentId === typeId,
              )
              if (foundAppointment) {
                eventDetails = {
                  type: 'update_appointment_details',
                  event: foundAppointment,
                }
                break
              }
            }

            break
          case 'lastMinutes':
            break
          case 'block': {
            const foundBlock = selectedDateEvents.blocks.find(
              block => block.id === typeId,
            )
            if (foundBlock) {
              eventDetails = {
                type: 'update_block',
                event: foundBlock,
              }
              break
            }
            setActiveEventGroupId(undefined)
            break
          }
          default:
            break
        }

        if (eventDetails) {
          dispatch(setOpenEventDetails({ openFormDetails: eventDetails }))
          setActiveEventGroupId(undefined)
        }
      }
    },
    [activeEventGroupId, events, dispatch],
  )

  if (calendarStatus === 'loading') return null

  return (
    <DragAndDropArea>
      {filteredPlannedEvents.map(plannedEvent => {
        const currentColumn: number = currentColumnCalculation(
          plannedEvent,
          filteredPersons,
          selectedStartDate,
        )

        return (
          <MemoizedEvent
            key={plannedEvent.id}
            event={plannedEvent}
            columnWidth={columnWidth}
            cellHeight={pixelsPerHour / (60 / timeDivision)}
            pixelsPerHour={pixelsPerHour}
            currentColumn={currentColumn}
            isActive={plannedEvent.groupId === activeEventGroupId}
            onClicked={onEventClicked}
            selectedStartDate={selectedStartDate}
          />
        )
      })}
    </DragAndDropArea>
  )
}
